<script setup lang="ts">
import { upperFirst } from 'lodash';
import { computed, defineAsyncComponent } from 'vue';

import type {
  AppSvgIconColor,
  AppSvgIconRotate,
  AppSvgIconSize,
  AppSvgIconTranslate,
  AppSvgIconType,
  AppSvgIconTransformedProps,
} from './AppSvgIcon.model';
import { transformPropsFormat } from './AppSvgIcon.model';

interface AppSvgIconInnerProps {
  name: AppSvgIconType;
  translate?: AppSvgIconTranslate;
  rotate?: AppSvgIconRotate;
  size?: AppSvgIconSize;
  color?: AppSvgIconColor;
}

const props = defineProps<AppSvgIconInnerProps>();

const settings = computed<AppSvgIconTransformedProps>(() =>
  transformPropsFormat(
    props.name,
    props.translate,
    props.rotate,
    props.size,
    props.color
  )
);

const dynamicComponent = computed(() => {
  // NOTE:
  // Be catious when doing changes here.
  // We are using this Vite plugin to make those dynamic imports work:
  // https://www.npmjs.com/package/vite-plugin-dynamic-import
  const name = upperFirst(props.name);
  return defineAsyncComponent(() => import(`./icons/${name}.vue`));
});
</script>

<template>
  <Suspense>
    <component
      :is="dynamicComponent"
      :width="settings.width"
      :height="settings.height"
      :viewBox="settings.viewBox"
      :style="settings.style"
      :class="props.name"
    />
    <template #fallback>
      <div :style="settings.style" :class="[[props.name], 'fallback']" />
    </template>
  </Suspense>
</template>

<style lang="scss" scoped>
@media print {
  svg {
    // NOTE: Forces icons to be dark in PDF document preview
    fill: var(--color-primary-20) !important;
  }
}

.fallback {
  height: v-bind('settings.height');
  width: v-bind('settings.width');
}
</style>
